import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

export default function ProjectTags({ board, api, onDataChange }) {
  const [tags, setTags] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState({
    name: '',
    color: '#3B82F6'
  });
  
  const [editingTag, setEditingTag] = useState(null);

  const colorOptions = [
    { name: 'Azul', value: '#3B82F6' },
    { name: 'Verde', value: '#10B981' },
    { name: 'Vermelho', value: '#EF4444' },
    { name: 'Amarelo', value: '#F59E0B' },
    { name: 'Roxo', value: '#8B5CF6' },
    { name: 'Rosa', value: '#EC4899' },
    { name: 'Cinza', value: '#6B7280' },
    { name: 'Laranja', value: '#F97316' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Teal', value: '#14B8A6' }
  ];

  useEffect(() => {
    if (board) {
      fetchTags();
    }
  }, [board]);

  const fetchTags = async () => {
    if (!board) return;
    
    try {
      const response = await api.get(`/projects/boards/${board.id}/tags`);
      setTags(response.data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) {
      toast.error('Nome da tag é obrigatório');
      return;
    }

    try {
      const response = await api.post(`/projects/boards/${board.id}/tags`, {
        name: newTag.name.trim(),
        color: newTag.color
      });
      
      setTags([...tags, response.data]);
      setNewTag({ name: '', color: '#3B82F6' });
      setShowCreateModal(false);
      toast.success('Tag criada com sucesso!');
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error('Erro ao criar tag');
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !editingTag.name.trim()) {
      toast.error('Nome da tag é obrigatório');
      return;
    }

    try {
      const response = await api.put(`/projects/tags/${editingTag.id}`, {
        name: editingTag.name.trim(),
        color: editingTag.color
      });
      
      setTags(tags.map(tag => tag.id === editingTag.id ? response.data : tag));
      setEditingTag(null);
      toast.success('Tag atualizada com sucesso!');
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error('Erro ao atualizar tag');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!confirm('Tem certeza que deseja excluir esta tag? Ela será removida de todos os itens.')) return;

    try {
      await api.delete(`/projects/tags/${tagId}`);
      setTags(tags.filter(tag => tag.id !== tagId));
      toast.success('Tag excluída com sucesso!');
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Erro ao excluir tag');
    }
  };

  const resetNewTag = () => {
    setNewTag({ name: '', color: '#3B82F6' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando tags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tags do Projeto</h2>
          <p className="text-gray-600">Organize seus itens com etiquetas coloridas</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Nova Tag
        </Button>
      </div>

      {/* Tags List */}
      {tags.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma tag criada</h3>
              <p className="text-gray-600 mb-6">
                Crie tags para organizar e categorizar seus itens de projeto
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                Criar Primeira Tag
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tags.map((tag) => (
            <Card key={tag.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="font-medium text-gray-900 truncate">{tag.name}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <Badge 
                    style={{ 
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                      borderColor: tag.color
                    }}
                    className="text-xs"
                  >
                    {tag.name}
                  </Badge>
                  
                  <div className="flex space-x-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingTag({...tag})}
                      className="h-6 w-6 p-0"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteTag(tag.id)}
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Tag Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) resetNewTag();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Nova Tag</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name">Nome da Tag</Label>
              <Input
                id="tag-name"
                value={newTag.name}
                onChange={(e) => setNewTag({...newTag, name: e.target.value})}
                placeholder="Ex: Urgente, Bug, Feature..."
                maxLength={20}
              />
            </div>

            <div className="space-y-2">
              <Label>Cor da Tag</Label>
              <div className="grid grid-cols-5 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setNewTag({...newTag, color: color.value})}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      newTag.color === color.value 
                        ? 'border-gray-900 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Preview:</p>
              <Badge 
                style={{ 
                  backgroundColor: `${newTag.color}20`,
                  color: newTag.color,
                  borderColor: newTag.color
                }}
              >
                {newTag.name || 'Nome da Tag'}
              </Badge>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateTag} disabled={!newTag.name.trim()}>
                Criar Tag
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tag Modal */}
      <Dialog open={!!editingTag} onOpenChange={(open) => {
        if (!open) setEditingTag(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tag</DialogTitle>
          </DialogHeader>
          
          {editingTag && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tag-name">Nome da Tag</Label>
                <Input
                  id="edit-tag-name"
                  value={editingTag.name}
                  onChange={(e) => setEditingTag({...editingTag, name: e.target.value})}
                  placeholder="Ex: Urgente, Bug, Feature..."
                  maxLength={20}
                />
              </div>

              <div className="space-y-2">
                <Label>Cor da Tag</Label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setEditingTag({...editingTag, color: color.value})}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        editingTag.color === color.value 
                          ? 'border-gray-900 scale-110' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                <Badge 
                  style={{ 
                    backgroundColor: `${editingTag.color}20`,
                    color: editingTag.color,
                    borderColor: editingTag.color
                  }}
                >
                  {editingTag.name || 'Nome da Tag'}
                </Badge>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setEditingTag(null)}>
                  Cancelar
                </Button>
                <Button onClick={handleUpdateTag} disabled={!editingTag.name.trim()}>
                  Salvar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}